package com.eladfinish.bible.contest.screens.search.suggestions

import com.eladfinish.bible.contest.network.ApiBaseUrlProvider
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.usecase.GetRemoteConfigObjectUseCase
import com.eladfinish.bible.contest.usecase.GetRemoteConfigValueUseCase
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer

/**
 * The Remote Config side of the search suggestions. Every value has a default, so the feature
 * works with no parameter created in Firebase; `disable_search_suggestions` is the kill switch,
 * on the negative so that a missing parameter means "on".
 */
data class SearchSuggestionsConfig(

    /** `disable_search_suggestions`: true hides the whole feature. */
    val isDisabled: Boolean = false,

    /** `search_suggestions_max`: how many suggestions to show while typing. */
    val maxSuggestions: Int = DEFAULT_MAX_SUGGESTIONS,

    /** `search_suggestions_url`: where the snapshot is downloaded from. */
    val snapshotUrl: String,

    /**
     * `search_suggestions_blocklist`: a JSON array of expressions never to suggest, compared
     * exactly to the expression as typed. The server applies the same list when it builds the
     * snapshot; applying it here too makes a new entry take effect on the next app launch.
     */
    val blocklist: Set<String> = emptySet(),
) {

    companion object {
        const val DEFAULT_MAX_SUGGESTIONS = 10
        const val SNAPSHOT_PATH = "/suggestions/snapshot.json"
    }
}

fun interface SearchSuggestionsConfigProvider {

    fun config(): SearchSuggestionsConfig
}

class RemoteConfigSearchSuggestionsConfigProvider(
    private val remoteConfigValue: GetRemoteConfigValueUseCase,
    private val remoteConfigObject: GetRemoteConfigObjectUseCase,
    private val apiBaseUrlProvider: ApiBaseUrlProvider,
) : SearchSuggestionsConfigProvider {

    override fun config(): SearchSuggestionsConfig {
        // Firebase answers a missing key with false / 0 / "", never null, so those are "unset".
        val max = remoteConfigValue.get<Long>(RemoteConfig.SEARCH_SUGGESTIONS_MAX)?.toInt()?.takeIf { it > 0 }
        val url = remoteConfigValue.get<String>(RemoteConfig.SEARCH_SUGGESTIONS_URL)?.takeIf { it.isNotBlank() }
        val blocklist = remoteConfigObject(
            remoteConfig = RemoteConfig.SEARCH_SUGGESTIONS_BLOCKLIST,
            serializer = ListSerializer(String.serializer()),
        ).orEmpty()

        return SearchSuggestionsConfig(
            isDisabled = remoteConfigValue.get<Boolean>(RemoteConfig.DISABLE_SEARCH_SUGGESTIONS) ?: false,
            maxSuggestions = max ?: SearchSuggestionsConfig.DEFAULT_MAX_SUGGESTIONS,
            snapshotUrl = url ?: (apiBaseUrlProvider.getBaseUrl() + SearchSuggestionsConfig.SNAPSHOT_PATH),
            blocklist = blocklist.filter { it.isNotEmpty() }.toSet(),
        )
    }
}
